import React, { useRef, useState } from "react";
import styled from "styled-components";
import { DEFAULT_PROJECT_PALETTE, PROJECT_PALETTES } from "@lifeat/shared/projects/ProjectsConstants";
import { translateProjectValidationError, validateProjectTitle } from "@lifeat/shared/projects/ProjectsUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { PopUpMenuComponents } from "@common/LifeAtPopupMenuV2/PopUpMenuComponents";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import { useToastListContext } from "@contexts/ToastListContext";
import Styles from "@themes/Styles";
import { darkTheme } from "@themes/Theme";
import { TagActionButtonRow, TagMenuFooter } from "../../task-management/components/TagDetailMenu";
import {
    ColorInputItem,
    ColorPreview,
    ColorSelectorLabelContainer,
    InputSectionContainer,
    TagCreationContentContainer,
} from "../../task-management/components/TagDetailMenuContent";
import { CreateProjectEvent, ProjectCreationSource } from "../AmplitudeProjectsEvents";
import { projectList$ } from "../ProjectGlobals";
import { ProjectsService } from "../ProjectsService";

const CancelRow = styled.div`
    display: flex;
    width: 100%;
    justify-content: flex-end;
`;

const CREATE_PROJECT_LABEL = "Create";
const PROJECT_TITLE_INPUT_LABEL = "Project name";

const DIVIDER_WIDTH_CSS_VALUE = "100%";

interface ProjectCreationMenuProps {
    readonly handleClosePopup: () => void;
    readonly amplitudeSource: ProjectCreationSource;
}
export default function ProjectCreationMenu(props: ProjectCreationMenuProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { addToastNotification } = useToastListContext();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const [projectTitleErrorMessage, setProjectTitleErrorMessage] = useState<string | undefined>();

    const [projectColor, setProjectColor] = useState<(typeof PROJECT_PALETTES)[number]["primary"]>(
        DEFAULT_PROJECT_PALETTE.primary,
    );
    const [projectTitle, setProjectTitle] = useState("");

    const [isCreateLoading, setIsCreateLoading] = useState(false);

    const handleValidateAndUpdateProjectTitle = (updatedProjectTitle: string) => {
        // if tag name validation fails, display the error message
        // and continue to update input field state
        const validateProjectNameResponse = validateProjectTitle(updatedProjectTitle);
        if (validateProjectNameResponse.isValid === false) {
            setProjectTitleErrorMessage(
                translateProjectValidationError(validateProjectNameResponse.reason, updatedProjectTitle),
            );
        } else {
            setProjectTitleErrorMessage(undefined);
        }

        setProjectTitle(updatedProjectTitle);
    };

    const handleCreateProject = () => {
        setIsCreateLoading(true);

        amplitudeService.logEvent(
            new CreateProjectEvent({
                numProjects: Object.keys(projectList$ || {}).length,
                source: props.amplitudeSource,
                title: projectTitle,
                color: projectColor,
            }),
        );

        ProjectsService.createNewProject({ title: projectTitle, color: projectColor })
            .then(() => {
                props.handleClosePopup();
            })
            .catch((error) => {
                addToastNotification("Failed to create project, please try again", error.message);
            });
    };

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <TagCreationContentContainer>
                <InputSectionContainer>
                    <CancelRow>
                        <PopUpMenuComponents.CloseButton />
                    </CancelRow>

                    <LifeAtTextInputV2
                        label={PROJECT_TITLE_INPUT_LABEL}
                        value={projectTitle}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleValidateAndUpdateProjectTitle(e.target.value)
                        }
                        onClick={() => inputRef.current?.focus()}
                        errorMessage={projectTitleErrorMessage}
                        showBorder={true}
                        ref={inputRef}
                    />
                </InputSectionContainer>

                <HorizontalDivider widthCssValue={DIVIDER_WIDTH_CSS_VALUE} />

                <InputSectionContainer>
                    {PROJECT_PALETTES.map((colorPalette) => {
                        return (
                            <ColorInputItem key={`project-color-option-${colorPalette.label}`}>
                                <input
                                    type="radio"
                                    value={colorPalette.primary}
                                    checked={colorPalette.primary === projectColor}
                                    id={`project-color-option-${colorPalette.label}`}
                                    onChange={() => setProjectColor(colorPalette.primary)}
                                />
                                <ColorSelectorLabelContainer htmlFor={`project-color-option-${colorPalette.label}`}>
                                    <ColorPreview colorHex={colorPalette.primary} />
                                    <LifeAtBodyPrimary>{colorPalette.label}</LifeAtBodyPrimary>
                                </ColorSelectorLabelContainer>
                            </ColorInputItem>
                        );
                    })}
                </InputSectionContainer>
            </TagCreationContentContainer>

            <TagMenuFooter>
                <HorizontalDivider widthCssValue={"100%"} />
                <TagActionButtonRow>
                    <LifeAtButtonV2
                        onClick={handleCreateProject}
                        isDisabled={!validateProjectTitle(projectTitle).isValid}
                        variant={"solid"}
                        overrideBackgroundColor={Styles.buttonFillBlue}
                        overrideTextColor={darkTheme.textColorPrimary}
                        disableHover={true}
                        isLoading={isCreateLoading}
                    >
                        {CREATE_PROJECT_LABEL}
                    </LifeAtButtonV2>
                </TagActionButtonRow>
            </TagMenuFooter>
        </div>
    );
}
